import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, takeWhile} from 'rxjs/operators';
import {User} from '../../../../core/src/lib/data/admin-panel';
import {AdminPanelService} from '../../../../core/src/lib/mock/admin-panel.service';
import { StoreService } from "../../../../core/src/lib/store/store.service";
import { APISeller } from "../../../../core/src/lib/api/api.types";
import { ApiService } from "../../../../core/src/lib/api/api.service";
import { Router } from "@angular/router";
import { NbToastrService } from "@nebular/theme";

@Component({
  selector: 'ngx-manage-sellers',
  styleUrls: ['./manage-sellers.component.scss'],
  templateUrl: './manage-sellers.component.html',
})
export class ManageSellersComponent implements OnInit, OnDestroy {

  private alive = true;
  active = true;

  options: APISeller[] = [];
  filteredOptions$?: Observable<APISeller[]>;

  superadmin = true;

  orgFilter: 'all' | 'operating' | 'member' = 'all';

  reconcilingIds = new Set<number>();

  @ViewChild('autoInput') input?: ElementRef<HTMLInputElement>;

  constructor(
    public store: StoreService,
    private router: Router,
    private api: ApiService,
    private toastr: NbToastrService,
  ) {
  }

  ngOnInit() {
    this.store.user$.pipe(takeWhile(() => this.alive)).subscribe(user => {
      this.superadmin = user?.role.permissionLevel === 3;
      this.options = this.store.availableSellers
    })

  }

  ngOnDestroy() {
    this.alive = false;
  }

  private filter(value: string): APISeller[] {
    const filterValue = value.toLowerCase();
    return this.options.filter(optionValue => optionValue.name.toLowerCase().includes(filterValue));
  }

  getFilteredOptions(value: string): Observable<APISeller[]> {
    return of(value).pipe(
      map(filterString => this.filter(filterString)),
    );
  }

  onChange() {
    this.filteredOptions$ = this.getFilteredOptions(this.input?.nativeElement.value ?? '');
  }

  onSelectionChange($event: APISeller) {
    this.sellerSettings($event)
  }

  sellerSettings(seller: APISeller) {
    this.store.setSeller(seller);
    this.router.navigate(['pages', 'settings']);
  }

  selectSeller(seller: APISeller) {
    this.store.setSeller(seller);
    this.router.navigate(['pages', 'home']);
  }

  viewOrganization(seller: APISeller, event: Event) {
    event.stopPropagation();
    this.store.setSeller(seller);
    this.router.navigate(['pages', 'organization-details']);
  }

  filterSellers(sellers: APISeller[]): APISeller[] {
    if (this.orgFilter === 'operating') {
      return sellers.filter(s => (s as any).isOperatingMerchant === true);
    }
    if (this.orgFilter === 'member') {
      return sellers.filter(s => (s as any).parentSellerId != null);
    }
    return sellers;
  }

  setOrgFilter(filter: 'all' | 'operating' | 'member') {
    this.orgFilter = filter;
  }

  isOperatingMerchant(seller: APISeller): boolean {
    return (seller as any).isOperatingMerchant === true;
  }

  isMemberMerchant(seller: APISeller): boolean {
    return (seller as any).parentSellerId != null;
  }

  isReconciling(seller: APISeller): boolean {
    return this.reconcilingIds.has(seller.id);
  }

  reconcileSeller(seller: APISeller, event: Event) {
    // Card-level click selects the seller; the button must not bubble.
    event.stopPropagation();
    if (this.reconcilingIds.has(seller.id)) {
      return;
    }
    this.reconcilingIds.add(seller.id);
    // `takeWhile(() => this.alive)` ties the subscription to the component
    // lifecycle so a late response after navigation can't fire a toast on
    // a destroyed component. `reconcilingIds` is owned by this instance,
    // so dropping the cleanup on destruction is fine.
    this.api
      .reconcileOrganization(seller.id)
      .pipe(takeWhile(() => this.alive))
      .subscribe({
        next: (result: any) => {
          this.reconcilingIds.delete(seller.id);
          if (result?.outcome === 'skipped') {
            this.toastr.warning(
              `Reconcile skipped (${result.reason ?? 'unknown reason'})`,
              seller.name,
            );
            return;
          }
          const added = Array.isArray(result?.added) ? result.added.length : 0;
          const removed = Array.isArray(result?.removed) ? result.removed.length : 0;
          this.toastr.success(
            `Reconcile complete (+${added}/-${removed})`,
            seller.name,
          );
        },
        error: (err: any) => {
          this.reconcilingIds.delete(seller.id);
          const message =
            err?.error?.message || err?.message || 'Reconcile failed';
          this.toastr.danger(message, seller.name);
        },
      });
  }
}
